import { useEffect, useRef, useCallback } from "react";
import { toast } from "react-toastify";
import { getAuthUser } from "../components/GetLocalStorage";
import { URLS } from "../constants";
import logger from "../utils/logger";
import { classifyWebSocketMessage } from "../utils/webSocketMessageClassifier";

/**
 * Hook global de WebSocket que conecta assim que o usuário faz login
 * e mantém a conexão ativa em todas as páginas autenticadas.
 *
 * Este hook:
 * - Conecta automaticamente ao fazer login
 * - Reconecta automaticamente em caso de desconexão
 * - Emite eventos globais para outros componentes consumirem
 * - Funciona em TODAS as rotas autenticadas
 */
export const useGlobalWebSocket = () => {
  const ws = useRef<WebSocket | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout>();
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 10;
  const isConnecting = useRef(false);
  const pendingSendsRef = useRef<any[]>([]);

  const connect = useCallback(() => {
    if (
      isConnecting.current ||
      (ws.current && ws.current.readyState === WebSocket.OPEN)
    ) {
      return;
    }

    const authUser = getAuthUser();

    // O sistema usa COOKIES para autenticação (auth_token), não localStorage
    // O WebSocket deve enviar cookies automaticamente via withCredentials/credentials
    // Vamos apenas verificar se o usuário está logado
    if (!authUser) {
      return;
    }

    try {
      isConnecting.current = true;

      //  Extrair access_token do cookie para enviar na URL do WebSocket
      const getCookie = (name: string) => {
        const match = document.cookie.match(
          new RegExp("(^| )" + name + "=([^;]+)"),
        );
        return match ? match[2] : null;
      };
      const accessToken = getCookie("access_token");

      // SSOR: Choose between agent-proxy (new) or service (legacy) based on feature flag
      // Note: USE_AGENT_PROXY is a string that gets replaced at runtime via entrypoint.sh
      let wsURL: string;
      if (URLS.USE_AGENT_PROXY === "true" && URLS.AGENT_PROXY_WS_URL) {
        // SSOR Direct Connection: Connect to agent-proxy for /chats page
        const companyId = authUser.companyID;
        const staffId = authUser.id || `staff-${authUser.companyID}`;
        wsURL = `${URLS.AGENT_PROXY_WS_URL}/api/agent/websocket/chats/${companyId}/${staffId}`;
      } else {
        // Legacy: Connect to service
        wsURL = `${URLS.WEBSOCKET_URL}/api/ws/web_client/messages`;
        if (accessToken) {
          wsURL += `?token=${encodeURIComponent(accessToken)}`;
        }
      }

      ws.current = new WebSocket(wsURL);

      ws.current.onopen = (event) => {
        isConnecting.current = false;
        reconnectAttempts.current = 0;

        // O backend autentica via cookies (auth_token)
        // Não precisamos enviar nada, apenas aguardar mensagens

        //  Flush de mensagens pendentes (caso UI tenha solicitado envio antes da conexão abrir)
        // Use event.target to get the correct WebSocket instance that fired this event
        // (avoids race condition when ws.current points to a newer reconnecting socket)
        const socket = event.target as WebSocket;
        if (
          pendingSendsRef.current.length > 0 &&
          socket.readyState === WebSocket.OPEN
        ) {
          const queue = [...pendingSendsRef.current];
          pendingSendsRef.current = [];
          queue.forEach((payload) => {
            try {
              socket.send(JSON.stringify(payload));
            } catch (e) {
              logger.error("[GlobalWS] Failed to flush queued message:", e);
              // Re-queue the message if send fails
              pendingSendsRef.current.push(payload);
            }
          });
        }
      };

      ws.current.onmessage = (event) => {
        try {
          const rawData = event.data;
          const data = JSON.parse(rawData);

          // Verificar resposta de autenticação
          if (data.type === "auth.success" || data.event === "auth.success") {
            // Emit auth success event for UI components
            window.dispatchEvent(
              new CustomEvent("global-websocket-auth", {
                detail: { success: true },
              }),
            );
          } else if (
            data.type === "auth.error" ||
            data.event === "auth.error"
          ) {
            const errorMessage =
              data.message || data.error || "Authentication failed";
            logger.error("[GlobalWS] Authentication failed:", errorMessage);

            // Show user-friendly notification
            toast.error(`Connection error: ${errorMessage}`, {
              position: "top-right",
              autoClose: 5000,
              hideProgressBar: false,
              closeOnClick: true,
              pauseOnHover: true,
            });

            // Emit auth error event for UI components to handle
            window.dispatchEvent(
              new CustomEvent("global-websocket-auth", {
                detail: {
                  success: false,
                  error: errorMessage,
                },
              }),
            );

            // Close connection on auth error - don't reconnect with same credentials
            if (ws.current) {
              ws.current.close(4001, "Authentication failed");
            }

            // Clear reconnect attempts to prevent loop with bad credentials
            reconnectAttempts.current = maxReconnectAttempts;
          }

          // Emitir evento global para outros componentes consumirem
          window.dispatchEvent(
            new CustomEvent("global-websocket-message", {
              detail: data,
            }),
          );

          // Tratamento específico de eventos importantes
          if (
            data.type === "chat.initial_load" ||
            data.type === "chat.snapshot"
          ) {
            if (data.chats && Array.isArray(data.chats)) {
              window.dispatchEvent(
                new CustomEvent("websocket-initial-chats", {
                  detail: {
                    chats: data.chats,
                    source: data.type, // 'chat.initial_load' ou 'chat.snapshot'
                    sessionIds: data.sessionIds || [], // Incluir sessionIds do backend para workaround
                    totalCount: data.totalCount,
                    hasMore: data.hasMore,
                  },
                }),
              );
            }
          }

          if (data.type === "chat.updated" && data.chatId) {
            window.dispatchEvent(
              new CustomEvent("websocket-chat-updated", {
                detail: { chatId: data.chatId },
              }),
            );
          }

          if (data.event === "sync.progress") {
            window.dispatchEvent(
              new CustomEvent("websocket-sync-progress", {
                detail: data,
              }),
            );
          }

          // Detectar nova sessão WORKING
          if (data.status === "WORKING" && data.sessionID) {
            window.dispatchEvent(
              new CustomEvent("websocket-session-status", {
                detail: {
                  status: "WORKING",
                  sessionId: data.sessionID,
                  timestamp: data.timestamp,
                  message: data.message || "Session connected successfully",
                },
              }),
            );
          }

          // SSOR: Handle chatHistory response from agent (via agent-proxy)
          // This is received when a chat is selected and messages are requested
          if (
            data.requestType === "chatHistory" ||
            data.type === "chatHistory"
          ) {
            window.dispatchEvent(
              new CustomEvent("websocket-chat-history", {
                detail: {
                  messages: data.payload || [],
                  clientId: data.clientId,
                },
              }),
            );
          }

          // Classify the WebSocket message to determine what events to dispatch
          // This uses extracted logic from webSocketMessageClassifier.ts
          const classification = classifyWebSocketMessage(data);

          // SSOR: Dispatch sidebar-only updates to update chat list (sorting, unread count, etc.)
          if (classification.shouldDispatchSidebarUpdate) {
            window.dispatchEvent(
              new CustomEvent("websocket-chat-sidebar-update", {
                detail: {
                  chatId: data.chatId || data.clientId || data.data?.id,
                  data: data.data,
                },
              }),
            );
          }

          // Dispatch message-received for actual message events
          // NOTE: Sidebar updates NO LONGER dispatch websocket-message-received
          // because they don't have proper message IDs (data.id is the chat ID)
          // and this was causing message duplication
          if (classification.shouldDispatchMessageReceived) {
            // For userMessage from agent, transform payload to expected format
            const messageDetail =
              data.requestType === "userMessage"
                ? {
                    message: data.payload,
                    who: data.payload?.fromMe ? "assistant" : "user",
                    chatId: data.payload?.clientID,
                    sessionId: data.payload?.session,
                  }
                : data;

            window.dispatchEvent(
              new CustomEvent("websocket-message-received", {
                detail: messageDetail,
              }),
            );
          }
        } catch (error) {
          logger.error("[GlobalWS] ❌ Error parsing message:", error);
        }
      };

      ws.current.onerror = (error) => {
        logger.error("[GlobalWS] ❌ WebSocket error:", error);
        isConnecting.current = false;
      };

      ws.current.onclose = (event) => {
        isConnecting.current = false;
        ws.current = null;

        // Reconectar automaticamente (com backoff exponencial)
        if (reconnectAttempts.current < maxReconnectAttempts) {
          const delay = Math.min(
            1000 * Math.pow(2, reconnectAttempts.current),
            30000,
          );

          reconnectTimeoutRef.current = setTimeout(() => {
            reconnectAttempts.current++;
            connect();
          }, delay);
        } else {
          logger.error("[GlobalWS] ❌ Max reconnection attempts reached");
        }
      };
    } catch (error) {
      logger.error("[GlobalWS] ❌ Error creating WebSocket:", error);
      isConnecting.current = false;
    }
  }, []);

  //  Canal único para a UI solicitar envios via WS sem criar conexões duplicadas
  // Exemplo: window.dispatchEvent(new CustomEvent('global-websocket-send', { detail: { event: 'authenticate' } }))
  useEffect(() => {
    const handleSend = (evt: Event) => {
      const e = evt as CustomEvent;
      const payload = e.detail;
      if (!payload) return;

      // Se não estiver conectado, enfileirar e tentar conectar
      if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
        pendingSendsRef.current.push(payload);
        connect();
        return;
      }

      try {
        ws.current.send(JSON.stringify(payload));
      } catch (err) {
        logger.error("[GlobalWS] ❌ Failed to send WS message:", err);
      }
    };

    window.addEventListener(
      "global-websocket-send",
      handleSend as EventListener,
    );
    return () =>
      window.removeEventListener(
        "global-websocket-send",
        handleSend as EventListener,
      );
  }, [connect]);

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    if (ws.current) {
      ws.current.close();
      ws.current = null;
    }
    isConnecting.current = false;
  }, []);

  // Conectar ao montar
  useEffect(() => {
    connect();

    // Cleanup ao desmontar
    return () => {
      disconnect();
    };
  }, [connect, disconnect]);

  // Reconectar quando o usuário voltar para a aba (visibilitychange)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        if (!ws.current || ws.current.readyState !== WebSocket.OPEN) {
          connect();
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [connect]);

  return {
    isConnected: ws.current?.readyState === WebSocket.OPEN,
    reconnect: connect,
    disconnect,
  };
};
